import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import {
  capitalizeFirstLetterOfEachWord,
  convertAmountToWords,
} from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const WorkOrderInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
  selectedReportName,
}) => {
  const toWords = new ToWords();
  const path = window.location.pathname.split("/").filter(Boolean);
  const transactionName = selectedReportName || path[1];

  const organizationData = JSON.parse(localStorage.getItem("organisation"));

  const normalizedWorkOrderType = String(headDetails?.type || "")
    .trim()
    .toLowerCase();
  const workOrderType =
    normalizedWorkOrderType === "job work" ||
    normalizedWorkOrderType === "jobwork" ||
    normalizedWorkOrderType === "job"
      ? "Job Work"
      : normalizedWorkOrderType === "sales" || normalizedWorkOrderType === "sale"
        ? "Sales"
        : capitalizeFirstLetterOfEachWord(headDetails?.type || "");

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const details = [
    {
      label: "MR No",
      value: formatDate(headDetails.mr_no),
    },
    {
      label: "Drawing No",
      value: formatDate(headDetails.drawing_number),
    },
    { label: "Assigned Engineer", value: headDetails.assigned_engineer },
    {
      label: "Inspection Date",
      value: formatDate(headDetails.inspection_date),
    },
  ];

  const workOrderHeaderDetails = [
    {
      label: "Work Order Date",
      value: formatDate(headDetails.date || headDetails.created_at),
    },
    ...(workOrderType === "Sales"
      ? []
      : [
          { label: "DC No", value: headDetails?.dc_no || "" },
          { label: "DC Date", value: formatDate(headDetails?.dc_date) },
        ]),
    {
      label: "Sales Order No",
      value:
        headDetails?.sales_order_no || headDetails?.sales_order_number || "",
    },
    {
      label: "Sales Order Date",
      value: formatDate(headDetails?.
so_date),
    },
  ];

  const purchaseDetails = [
    { label: "Work Order No", value: headDetails.tran_no },
    {
      label: "Work Order Date",
      value: formatDate(headDetails.date || headDetails.created_at),
    },
  ];

  const notes = [
    "Test certificate / inspection report must accompany the returned material.",
    "Guarantee / Warranty certificates, if applicable, must be provided.",
    "Please courier the original MODVAT copies of invoices directly to us and not via the transporter.",
  ];

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const visibleFields = [...rowData]
    .filter((field) => !field.is_internal_field)
    .reverse();

  const isDeliveryDateField = (field) =>
    ["delivery_date", "expected_delivery", "expected_delivery_date"].includes(
      (field.field_name || "").toLowerCase(),
    ) || /delivery[\s_-]+date/i.test(field.title || "");
  const isEngineeringDrawingField = (field) =>
    /engineering[\s_-]+(?:dwg|drawing)/i.test(`${field.title || ""} ${field.field_name || ""}`);

  const tableFields = visibleFields
    .filter(
      (field) =>
        ![
          "Create Jobcard",
          "BOM",
          "Product Description",
          "Packing List",
        ].includes(field.title),
    )
    .filter((field) => {
      const fieldName = (field.field_name || "").toLowerCase();
      return !["source_work_order_no", "work_order_no", "wo_no", "part_no", "part_number", "remarks", "remark"].includes(
        fieldName,
      );
    });

  const engineeringDrawingField = tableFields.find(isEngineeringDrawingField);
  const deliveryDateField = tableFields.find(isDeliveryDateField) || {
    field_name: "delivery_date",
  };
  const orderedTableFields = engineeringDrawingField
    ? tableFields
        .filter((field) =>
          !isDeliveryDateField(field) &&
          (!isEngineeringDrawingField(field) || field === engineeringDrawingField),
        )
        .map((field) => field === engineeringDrawingField
          ? { ...deliveryDateField, title: "Delivery Date" }
          : field,
        )
    : [...tableFields];

  const workOrderColumnWeights = [8, 15, 11];
  orderedTableFields.forEach((field) => {
    const title = `${field.title || field.field_name || ""}`.toLowerCase();
    if (title.includes("product") || title.includes("description")) {
      workOrderColumnWeights.push(18);
    } else if (title.includes("quantity") || title.includes("qty")) {
      workOrderColumnWeights.push(13);
    } else if (title.includes("uom") || title.includes("unit")) {
      workOrderColumnWeights.push(9);
    } else if (title.includes("delivery") && title.includes("date")) {
      workOrderColumnWeights.push(12);
    } else if (title.includes("engineering") || title.includes("drawing")) {
      workOrderColumnWeights.push(15);
    } else {
      workOrderColumnWeights.push(12);
    }
  });
  workOrderColumnWeights.push(14);
  const workOrderColumnTotal = workOrderColumnWeights.reduce(
    (total, weight) => total + weight,
    0,
  );

  const communicatedColumns = [
    "Design",
    "Purchase",
    "Production",
    "Store",
    "Quality",
    "Account",
  ];

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 },
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();
  const finalTotal = totalAmountFromItems + parseFloat(totalGst);
  const amountInWords = convertAmountToWords(finalTotal, toWords);

  return (
    <div className="invoice-container workorder-layout">
      <div className="workorder-main">
        <div className="workorder-header-table">
          <div className="invoice-box">
            <div className="invoice-left">
              <div className="invoice-logo-container">
                <img
                  src={organizationData.logo_url || ProfilePlaceholder}
                  alt="Logo"
                  className="invoice-logo-content"
                />
              </div>
              <div className="workorder-company-text">
                <h3 className="invoice-company-name">
                  {organizationData.name || "N/A"}
                </h3>
              </div>
            </div>
            <div className="invoice-center">
              <h2 className="invoice-title">
                {capitalizeFirstLetterOfEachWord(transactionName || "")}
              </h2>
              <div className="workorder-type-row">
                <span className="workorder-type">{workOrderType}</span>
              </div>
            </div>
            <div className="invoice-right">
              {workOrderHeaderDetails.map((item, index) => (
                <div key={index} className="purchase-detail">
                  <p className="detail-label">
                    <strong>{item.label}:</strong>
                  </p>
                  <div className="detail-value">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="invoice-overflow-x-scroll">
          <table className="invoice-table">
            <colgroup>
              {workOrderColumnWeights.map((weight, index) => (
                <col
                  key={index}
                  style={{
                    width: `${(weight / workOrderColumnTotal) * 100}%`,
                  }}
                />
              ))}
            </colgroup>
            <thead className="invoice-thead">
              <tr>
                <th className="invoice-th" style={{ borderLeftWidth: "0px" }}>
                  No.
                </th>
                <th className="invoice-th">Work Order No</th>
                <th className="invoice-th">Part No</th>
                {orderedTableFields.map((field, index) => (
                  <th
                    key={index}
                    className="invoice-th"
                    style={
                      index === orderedTableFields.length - 1
                        ? { borderRightWidth: "0px" }
                        : {}
                    }
                  >
                    {field.title || field.field_name}
                  </th>
                ))}
                <th className="invoice-th" style={{ borderRightWidth: "0px" }}>
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {itemDetails.map((item, index) => (
                <tr key={index}>
                  <td className="invoice-td" style={{ borderLeftWidth: "0px" }}>
                    {index + 1}
                  </td>
                  <td className="invoice-td">
                    {item.source_work_order_no || item.work_order_no || item.wo_no || headDetails?.tran_no || "-"}
                  </td>
                  <td className="invoice-td">
                    {item.part_no || item.part_number || "-"}
                  </td>
                  {orderedTableFields.map((field, idx, arr) => (
                    <td
                      key={idx}
                      className="invoice-td"
                      style={
                        idx === arr.length - 1
                          ? { borderRightWidth: "0px" }
                          : {}
                      }
                    >
                      {isDeliveryDateField(field)
                        ? item[field.field_name] || item.delivery_date || item.expected_delivery || item.expected_delivery_date || headDetails.expected_delivery || "-"
                        : item[field.field_name] || "-"}
                    </td>
                  ))}
                  <td
                    className="invoice-td"
                    style={{ borderRightWidth: "0px" }}
                  >
                    {item.remarks || item.remark || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="workorder-communicated">
          <div className="workorder-communicated-header">Communicated to</div>
          <table className="workorder-communicated-table">
            <thead>
              <tr>
                {communicatedColumns.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {communicatedColumns.map((_, idx) => (
                  <td key={idx}></td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="invoice-footer-meta-block">
          <div
            className="workorder-header-row workorder-meta-row"
            style={{
              borderLeft: "1px solid #9ca3af",
              borderRight: "1px solid #9ca3af",
              borderTop: "none",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            }}
          >
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Prepared By:</span>
              <span className="workorder-meta-value">
                {headDetails?.prepared_by || ""}
              </span>
            </div>
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Revised By:</span>
              <span className="workorder-meta-value">
                {headDetails?.revised_by || ""}
              </span>
            </div>
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Approved By:</span>
              <span className="workorder-meta-value">
                {headDetails?.approved_by || ""}
              </span>
            </div>
          </div>
        </div>
        <div className="invoice-footer-meta-block">
          <div
            className="workorder-header-row workorder-meta-row"
            style={{
              borderLeft: "1px solid #9ca3af",
              borderRight: "1px solid #9ca3af",
              borderTop: "none",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            }}
          >
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Ref No:</span>
              <span className="workorder-meta-value">FP/F/8.2/04</span>
            </div>
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Revision No:</span>
              <span className="workorder-meta-value">
                {headDetails?.rev_no === "00" ? "01" : headDetails?.rev_no || "01"}
              </span>
            </div>
            <div className="workorder-meta-cell">
              <span className="workorder-meta-label">Revision Date:</span>
              <span className="workorder-meta-value">
                {headDetails?.rev_date
                  ? formatDate(headDetails.rev_date)
                  : "10-12-2023"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>
      <div className="invoice-buttons" style={{ paddingTop: "40px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".workorder-layout"
        />
      </div>
    </div>
  );
};
